import React from 'react';
import { Redirect, generatePath } from 'react-router-dom';

import { Profile } from '../../components/main/settings/profile/Profile';
import OrgProfileResolver from '../../components/main/settings/orgProfile/OrgProfileResolver';
import { CustomerIdentitySubrouter } from './CustomerIdentitySubrouter';
import AccountsIcon from '../../_assets/svg/Accounts.svg';
import AggregatorsIcon from '../../_assets/svg/Aggregators.svg';
import TenantsIcon from '../../_assets/svg/TenantsIcon.svg';

const RoleCreation = React.lazy(() => import('../../roles/RoleCreation'));
const Users = React.lazy(() => import('../admin/users/Users'));

/**
 * Keeps a bookmarked pre-Identity URL working instead of dropping to NotFound.
 *
 * Params carry over: `/customer/manage/roles/edit/:roleId` has to arrive at the
 * edit page for THAT role, not at the roles list. Every one of these is also
 * `exact` at its call site -- a non-exact `/customer/manage/roles` swallows
 * `/customer/manage/roles/edit/abc` and drops the id on the floor.
 */
const redirectTo = (to: string) => (props: any) =>
  React.createElement(Redirect, {
    to: generatePath(to, props?.match?.params ?? {}),
  });

export const useCustomerManageRoutes = () => {
  return React.useMemo(
    () => [
      {
        path: '/customer/manage/me/:tab?',
        allow: { user_profile: 'manage' },
        component: Profile,
        nav: {
          name: 'My Profile',
          groupOrder: 3,
          icon: React.createElement(AccountsIcon),
        },
      },
      {
        // Optional param. The sidebar builds an item's href by cutting the
        // path at '/:', so this route was linked as /customer/manage/profile
        // with no id -- which matched nothing and rendered a blank page. The
        // resolver already falls back to the actor's own account, so the bare
        // path is the more honest URL anyway.
        path: '/customer/manage/profile/:accountId?',
        allow: { account_profile: 'view' },
        component: OrgProfileResolver,
        nav: {
          name: 'Org. Profile',
          groupOrder: 0,
          icon: React.createElement(TenantsIcon),
        },
      },
      {
        // Listed before the (non-exact) Identity tab route so the Switch
        // matches it first.
        path: '/customer/manage/identity/roles/create',
        allow: { roles: 'manage' },
        component: RoleCreation,
      },
      {
        // Listed before the (non-exact) Identity tab route so the Switch
        // matches it first.
        path: '/customer/manage/identity/roles/edit/:roleId',
        allow: { roles: 'manage' },
        component: RoleCreation,
      },
      {
        path: '/customer/manage/identity/users/:userId',
        allow: { users: 'view' },
        rqps: ['size', 'orderBy', 'order'],
        component: Users,
      },
      {
        // Users and Roles as tabs of one page, the way the Cowbell console does
        // it. `allowEither`, not `allow`: a reader who can see only one of the
        // two still has a page worth opening.
        path: '/customer/manage/identity/:tab?',
        allowEither: [{ users: 'view' }, { roles: 'view' }],
        rqps: ['size', 'orderBy', 'order'],
        component: CustomerIdentitySubrouter,
        nav: {
          name: 'Users',
          groupOrder: 2,
          icon: React.createElement(AggregatorsIcon),
        },
      },
      // The pre-Identity URLs, kept as redirects so a bookmark or an emailed
      // link still lands on the right tab rather than on NotFound.
      {
        path: '/customer/manage/roles/create',
        exact: true,
        component: redirectTo('/customer/manage/identity/roles/create'),
      },
      {
        path: '/customer/manage/roles/edit/:roleId',
        exact: true,
        component: redirectTo('/customer/manage/identity/roles/edit/:roleId'),
      },
      {
        path: '/customer/manage/roles',
        exact: true,
        component: redirectTo('/customer/manage/identity/roles'),
      },
      {
        path: '/customer/manage/users',
        exact: true,
        component: redirectTo('/customer/manage/identity'),
      },
    ],
    []
  );
};
